import {
  Dismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getWorkTypes, type WorkType } from "../../clients/clientsApi";
import { SearchableSelect } from "../../components/SearchableSelect";
import { useLookupOptions } from "../../lookups/useLookupOptions";
import {
  createBudget,
  getFinancialSettings,
  type BudgetPayload,
} from "../../orcamentos/orcamentosApi";
import { createBudgetFromWorkType } from "../../tiposObra/tiposObraApi";
import { getUsers, type UserAdmin } from "../../users/usersApi";
import { APP_LOCALE } from "../../config/locale";
import { getBudgetTemplates, createBudgetFromTemplate, type BudgetTemplate } from "../../budgetTemplates/budgetTemplatesApi";
import { TemplateSelector } from "../../components/TemplateSelector";

interface CriarOrcamentoViewProps {
  fromEncomenda?: boolean;
}

export function CriarOrcamentoView({
  fromEncomenda = false,
}: CriarOrcamentoViewProps) {
  const navigate = useNavigate();
  const [modo, setModo] = useState<'doZero' | 'deTemplate' | 'deTipoObra'>('doZero');
  const [templates, setTemplates] = useState<BudgetTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>();
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [selectedWorkTypeId, setSelectedWorkTypeId] = useState<number | "">("");
  const [workArea, setWorkArea] = useState("");
  const [clienteId, setClienteId] = useState<number | "">("");
  const [nomeObra, setNomeObra] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [orderReference, setOrderReference] = useState("");
  const {
    options: clienteOptions,
    loading: isLoadingClientes,
    error: clientesError,
  } = useLookupOptions("clients");
  const [users, setUsers] = useState<UserAdmin[]>([]);
  // IVA padrão (valor percentual, ex.: 23) proveniente dos parâmetros financeiros.
  const [defaultVatRate, setDefaultVatRate] = useState<number>(23);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(
    null,
  );

  const responsaveisDisponiveis = useMemo(() => {
    return users
      .map((user) =>
        `${user.firstName} ${user.lastName}`.trim(),
      )
      .filter((name) => name !== "")
      .sort((a, b) => a.localeCompare(b, APP_LOCALE));
  }, [users]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const [usersData, workTypesData, financialSettings] =
          await Promise.all([
            getUsers(),
            getWorkTypes().catch(() => [] as WorkType[]),
            getFinancialSettings().catch(() => null),
          ]);

        if (!isMounted) return;

        setUsers(usersData);
        setWorkTypes(workTypesData);
        if (
          financialSettings &&
          typeof financialSettings.defaultVatRate === "number" &&
          financialSettings.defaultVatRate >= 0
        ) {
          setDefaultVatRate(financialSettings.defaultVatRate);
        }
      } catch (error) {
        if (!isMounted) return;
        setLoadError(
          "Não foi possível carregar os dados necessários para criar o orçamento.",
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (modo !== 'deTemplate') return;

    let isMounted = true;

    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const templatesData = await getBudgetTemplates();
        if (!isMounted) return;
        setTemplates(templatesData);
      } catch (error) {
        if (!isMounted) return;
        // Erro silencioso - TemplateSelector mostrará empty state
      } finally {
        if (isMounted) {
          setLoadingTemplates(false);
        }
      }
    };

    loadTemplates();

    return () => {
      isMounted = false;
    };
  }, [modo]);

  const handleCriar = async () => {
    if (nomeObra.trim() === "") {
      setSaveError(
        "Preencha o nome da obra antes de guardar.",
      );
      return;
    }

    if (modo === 'deTemplate' && !selectedTemplateId) {
      setSaveError(
        "Selecione um template.",
      );
      return;
    }

    if (modo === 'deTipoObra' && selectedWorkTypeId === "") {
      setSaveError(
        "Selecione um tipo de obra.",
      );
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      let budgetId: number;

      if (modo === 'deTemplate' && selectedTemplateId) {
        // Criar a partir de template
        budgetId = await createBudgetFromTemplate({
          templateId: selectedTemplateId,
          budgetName: nomeObra.trim(),
        });
      } else if (modo === 'deTipoObra' && selectedWorkTypeId !== "") {
        // Criar a partir de um tipo de obra + área
        const areaParsed = Number.parseFloat(
          workArea.trim().replace(",", "."),
        );
        budgetId = await createBudgetFromWorkType({
          workTypeId: selectedWorkTypeId,
          workArea: Number.isFinite(areaParsed) ? areaParsed : 0,
          workName: nomeObra.trim(),
          clientId: clienteId === "" ? null : clienteId,
          responsible: responsavel.trim() || null,
          orderReference: orderReference.trim() || null,
        });
      } else {
        // Criar do zero
        const payload: BudgetPayload = {
          reference: "",
          clientId: clienteId === "" ? null : clienteId,
          workName: nomeObra.trim(),
          responsible: responsavel.trim() || null,
          orderReference: orderReference.trim() || null,
          vatRate: defaultVatRate,
          lines: [],
          documents: [],
        };
        const budget = await createBudget(payload);
        budgetId = budget.id;
      }

      navigate(`/orcamentos/editar/${budgetId}`, {
        state: { toast: "Orçamento criado com sucesso" },
      });
    } catch (error) {
      setSaveError(
        "Não foi possível criar o orçamento. Tente novamente.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const isFormValido =
    nomeObra.trim() !== "" &&
    (modo === 'doZero' ||
      (modo === 'deTemplate' && selectedTemplateId !== undefined) ||
      (modo === 'deTipoObra' && selectedWorkTypeId !== ""));

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/orcamentos")}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        disabled={isSaving || isLoading || !isFormValido}
        onClick={handleCriar}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Orçamento"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <PageHeader
        title="Criar Orçamento"
        subtitle="Crie um novo orçamento preenchendo as informações principais."
        actions={actions}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {/* Encomenda Source Info */}
          {fromEncomenda && (
            <div
              className="text-secondary-text mb-6"
              style={{
                fontSize: "var(--text-caption)",
                fontWeight: "var(--font-weight-normal)",
              }}
            >
              Criado a partir de uma Encomenda
            </div>
          )}

          {loadError && (
            <div
              className="mb-6 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
              style={{ borderRadius: "var(--radius)" }}
            >
              {loadError}
            </div>
          )}

          {saveError && (
            <div
              className="mb-6 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
              style={{ borderRadius: "var(--radius)" }}
            >
              {saveError}
            </div>
          )}

          <div className="max-w-4xl mx-auto">
            {/* Card: Dados Principais do Orçamento */}
            <div
              className="bg-card border border-border p-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h3
                className="text-foreground mb-6"
                style={{
                  fontSize: "var(--text-section-title)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Dados Principais do Orçamento
              </h3>

              <div className="space-y-5">
                {/* Modo: Do Zero ou De Template */}
                <div>
                  <label
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight:
                        "var(--font-weight-semi-bold)",
                    }}
                  >
                    Tipo de Criação
                  </label>
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="modo"
                        value="doZero"
                        checked={modo === 'doZero'}
                        onChange={() => setModo('doZero')}
                        disabled={isLoading}
                        className="w-4 h-4"
                      />
                      <span className="text-foreground" style={{ fontSize: "var(--text-base)" }}>
                        Do Zero
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="modo"
                        value="deTemplate"
                        checked={modo === 'deTemplate'}
                        onChange={() => setModo('deTemplate')}
                        disabled={isLoading}
                        className="w-4 h-4"
                      />
                      <span className="text-foreground" style={{ fontSize: "var(--text-base)" }}>
                        De Template
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="modo"
                        value="deTipoObra"
                        checked={modo === 'deTipoObra'}
                        onChange={() => setModo('deTipoObra')}
                        disabled={isLoading}
                        className="w-4 h-4"
                      />
                      <span className="text-foreground" style={{ fontSize: "var(--text-base)" }}>
                        A partir de tipo de obra
                      </span>
                    </label>
                  </div>
                  <p
                    className="mt-1 text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    {modo === 'doZero'
                      ? 'Criar orçamento vazio e adicionar itens manualmente'
                      : modo === 'deTemplate'
                        ? 'Criar orçamento baseado num template existente'
                        : 'Gerar a estrutura inicial a partir do template do tipo de obra e da área (m²)'}
                  </p>
                </div>

                {/* Template Selector (apenas se modo === 'deTemplate') */}
                {modo === 'deTemplate' && (
                  <div>
                    <TemplateSelector
                      templates={templates}
                      selectedTemplateId={selectedTemplateId}
                      onChange={setSelectedTemplateId}
                      loading={loadingTemplates}
                      disabled={isSaving}
                    />
                  </div>
                )}

                {/* Tipo de obra + área (apenas se modo === 'deTipoObra') */}
                {modo === 'deTipoObra' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="tipoObra"
                        className="block text-foreground mb-2"
                        style={{ fontSize: "var(--text-caption)", fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        Tipo de Obra <span className="text-primary">*</span>
                      </label>
                      <select
                        id="tipoObra"
                        value={selectedWorkTypeId}
                        onChange={(e) => setSelectedWorkTypeId(e.target.value === "" ? "" : Number.parseInt(e.target.value, 10))}
                        disabled={isLoading || isSaving}
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ borderRadius: "var(--radius-button)", fontSize: "var(--text-base)" }}
                      >
                        <option value="">Selecione um tipo de obra</option>
                        {workTypes.map((workType) => (
                          <option key={workType.id} value={workType.id}>
                            {workType.name}{typeof workType.templateLineCount === "number" ? ` (${workType.templateLineCount} linhas)` : ""}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-secondary-text" style={{ fontSize: "var(--text-caption)" }}>
                        O template do tipo de obra é gerido no Catálogo → Tipos de obra.
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="workArea"
                        className="block text-foreground mb-2"
                        style={{ fontSize: "var(--text-caption)", fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        Área (m²)
                      </label>
                      <input
                        id="workArea"
                        type="number"
                        min="0"
                        step="0.01"
                        value={workArea}
                        onChange={(e) => setWorkArea(e.target.value)}
                        disabled={isLoading || isSaving}
                        placeholder="Ex: 120"
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ borderRadius: "var(--radius-button)", fontSize: "var(--text-base)" }}
                      />
                      <p className="mt-1 text-secondary-text" style={{ fontSize: "var(--text-caption)" }}>
                        As linhas marcadas como “por m²” serão multiplicadas por esta área.
                      </p>
                    </div>
                  </div>
                )}

                {/* Cliente (apenas modo "Do Zero") */}
                {modo !== 'deTemplate' && (
                <div>
                  <label
                    htmlFor="cliente"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight:
                        "var(--font-weight-semi-bold)",
                    }}
                  >
                    Cliente{" "}
                    <span className="text-secondary-text">
                      (opcional)
                    </span>
                  </label>
                  <SearchableSelect<number>
                    id="cliente"
                    value={clienteId === "" ? null : clienteId}
                    onChange={(next) => setClienteId(next ?? "")}
                    options={clienteOptions}
                    loading={isLoadingClientes}
                    disabled={isLoading}
                    placeholder="Selecione um cliente"
                    searchPlaceholder="Pesquisar cliente..."
                    error={
                      clientesError
                        ? "Não foi possível carregar os clientes."
                        : undefined
                    }
                  />
                  <p
                    className="mt-1 text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Selecione o cliente para o qual está a criar
                    este orçamento (opcional).
                  </p>
                </div>
                )}

                {/* Nome da Obra */}
                <div>
                  <label
                    htmlFor="nomeObra"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight:
                        "var(--font-weight-semi-bold)",
                    }}
                  >
                    Nome da Obra{" "}
                    <span className="text-primary">*</span>
                  </label>
                  <input
                    id="nomeObra"
                    type="text"
                    value={nomeObra}
                    onChange={(e) =>
                      setNomeObra(e.target.value)
                    }
                    disabled={isLoading}
                    placeholder="Ex: Remodelação Edifício Pombalino"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                  />
                  <p
                    className="mt-1 text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Dê um nome descritivo para identificar
                    facilmente a obra
                  </p>
                </div>

                {/* Referência Encomenda (Externa) - apenas modo "Do Zero" */}
                {modo !== 'deTemplate' && (
                <div>
                  <label
                    htmlFor="orderReference"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight:
                        "var(--font-weight-semi-bold)",
                    }}
                  >
                    Referência Encomenda (Externa)
                  </label>
                  <input
                    id="orderReference"
                    type="text"
                    value={orderReference}
                    onChange={(e) =>
                      setOrderReference(e.target.value)
                    }
                    disabled={isLoading}
                    placeholder="Ex: ENQ-2024-001 / Concurso 34/2024"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                  />
                  <p
                    className="mt-1 text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Referência da encomenda do cliente ou número
                    de concurso
                  </p>
                </div>
                )}

                {/* Responsável Técnico - apenas modo "Do Zero" */}
                {modo !== 'deTemplate' && (
                <div>
                  <label
                    htmlFor="responsavel"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight:
                        "var(--font-weight-semi-bold)",
                    }}
                  >
                    Responsável Técnico
                  </label>
                  <select
                    id="responsavel"
                    value={responsavel}
                    onChange={(e) =>
                      setResponsavel(e.target.value)
                    }
                    disabled={isLoading}
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                  >
                    <option value="">
                      Selecione um responsável
                    </option>
                    {responsaveisDisponiveis.map((nome) => (
                      <option key={nome} value={nome}>
                        {nome}
                      </option>
                    ))}
                  </select>
                  <p
                    className="mt-1 text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Responsável técnico pela elaboração e
                    acompanhamento do orçamento
                  </p>
                </div>
                )}

                {/* Info Box */}
                <div
                  className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <p
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-base)",
                      fontWeight: "var(--font-weight-normal)",
                    }}
                  >
                    <span
                      style={{
                        fontWeight:
                          "var(--font-weight-semi-bold)",
                      }}
                    >
                      Próximo passo:
                    </span>{" "}
                    {modo === 'deTemplate'
                      ? 'O orçamento será criado com a estrutura completa do template selecionado. Poderá editá-lo livremente após a criação.'
                      : modo === 'deTipoObra'
                        ? 'O orçamento será gerado com os capítulos e artigos do tipo de obra, com as quantidades “por m²” já multiplicadas pela área. Poderá editá-lo livremente após a criação.'
                        : 'Após criar o orçamento, poderá adicionar capítulos, linhas, valores e documentos na página de edição.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
